import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { lazy, Suspense, useEffect, useState } from "react";
import { HelmetProvider } from "react-helmet-async";
import CookieConsent from "./components/CookieConsent";
import ScrollToTop from "./components/ScrollToTop";
import ErrorBoundary from "./components/ErrorBoundary";
import PageLoader from "./components/PageLoader";
import { pushEvent } from "@/utils/dataLayer";

// Deferred toast components — loaded after LCP, not during critical path
const Toaster = lazy(() => import("@/components/ui/toaster").then(m => ({ default: m.Toaster })));
const Sonner = lazy(() => import("@/components/ui/sonner").then(m => ({ default: m.Toaster })));

const DeferredToasts = () => {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if (typeof window !== "undefined" && window.location.pathname === "/") {
      return;
    }

    // Wait for idle time after LCP before loading toast infrastructure
    const id = typeof requestIdleCallback !== 'undefined'
      ? requestIdleCallback(() => setReady(true), { timeout: 8000 })
      : setTimeout(() => setReady(true), 8000) as unknown as number;
    return () => {
      if (typeof cancelIdleCallback !== 'undefined') cancelIdleCallback(id);
      else clearTimeout(id);
    };
  }, []);
  if (!ready) return null;
  return (
    <>
      <Suspense fallback={null}><Toaster /></Suspense>
      <Suspense fallback={null}><Sonner /></Suspense>
    </>
  );
};

// Eager load the home page for best performance
import Index from "./pages/Index";

// Lazy load all other pages
const Protocol = lazy(() => import("./pages/Protocol"));
const Infrastructure = lazy(() => import("./pages/Infrastructure"));
const Systems = lazy(() => import("./pages/Systems"));
const Blog = lazy(() => import("./pages/Blog"));
const Intake = lazy(() => import("./pages/Intake"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const Auth = lazy(() => import("./pages/Auth"));
const Admin = lazy(() => import("./pages/Admin"));
const DiagnosticPage = lazy(() => import("./pages/DiagnosticPage"));
const OptOutConfirmed = lazy(() => import("./pages/OptOutConfirmed"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

const PageViewTracker = () => {
  const location = useLocation();
  useEffect(() => {
    pushEvent('page_view', { page_path: location.pathname + location.search });
  }, [location.pathname, location.search]);
  return null;
};

const App = () => (
  <HelmetProvider>
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <DeferredToasts />
      <BrowserRouter>
        <ErrorBoundary>
          <a
            href="#main-content"
            className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-[100] focus:bg-primary focus:text-primary-foreground focus:px-4 focus:py-2 focus:font-mono focus:text-xs focus:font-bold focus:uppercase focus:tracking-wider"
          >
            Skip to content
          </a>
          <ScrollToTop />
          <PageViewTracker />
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/protocol" element={<Protocol />} />
              <Route path="/infrastructure" element={<Infrastructure />} />
              <Route path="/systems" element={<Systems />} />
              <Route path="/intake" element={<Intake />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<Blog />} />
              <Route path="/diagnostic/:slug" element={<DiagnosticPage />} />
              <Route path="/opt-out-confirmed" element={<OptOutConfirmed />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/admin/*" element={<Admin />} />
              {/* Redirects for old routes */}
              <Route path="/about" element={<Navigate to="/protocol" replace />} />
              <Route path="/portfolio" element={<Navigate to="/systems" replace />} />
              <Route path="/portfolio/:slug" element={<Navigate to="/systems" replace />} />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          <CookieConsent />
        </ErrorBoundary>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
  </HelmetProvider>
);

export default App;
